import { Loader, Primitive, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ContextModalProps } from "@mantine/modals";
import { ArrowClockwiseIcon, FloppyDiskIcon } from "@phosphor-icons/react";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { z } from "zod";

import { Input } from "@/components/@custom/Input";
import { useSession } from "@/hooks/@custom/useSession";
import api from "@/services/api";
import { Role } from "@/store";
import { useLazyGetOrdersQuery } from "@/store/apis/nextcargo/order";
import { handleFormatTxtToQty } from "@/utils/handleFormatTxtToQty";

import { ExpeditionsQueueStatusModalProps } from ".";
import { ExpeditionActionsBase } from "./base/ExpeditionActionsBase";
import { ExpeditionQueueListItemBase } from "./ExpeditionQueueListItemBase";
import { ExpeditionQueueStatusBase } from "./ExpeditionQueueStatusBase";
import { useVehiclesStatus } from "./useVehiclesStatus";

interface VehiclesMissingData {
  id: number;
  attended_at: string;
  driver: {
    id: number;
    name: string;
  };
  scheduling: {
    id: number;
    qtyTransported: number;
  };
  truck: {
    licensePlate: string;
  };
}

type ExpeditionsQueueStatusMissingModalProps = ExpeditionsQueueStatusModalProps & {};

export function ExpeditionsQueueStatusMissingModal({ context: _context, id: _id, innerProps: _innerProps }: ContextModalProps<ExpeditionsQueueStatusMissingModalProps>) {
  const { data, search, activeArray, state } = useVehiclesStatus<VehiclesMissingData>({
    type: "missing"
  });

  const renderItem = useCallback((attendance: VehiclesMissingData) => <MissingItem key={attendance.id} attendance={attendance} />, []);

  const list = useMemo(
    () => ({
      data: data[activeArray],
      render: renderItem
    }),
    [data, activeArray, renderItem]
  );

  const alertMessage = useMemo(
    () => (
      <Text size="xs">
        Atenção, o resgate será confirmado pelo <strong className="font-semibold">Supervisor do Sistema</strong>
      </Text>
    ),
    []
  );

  const slots = useMemo(
    () => ({
      search: { props: { searchValue: search.value, onSearchChange: search.onChange } },
      alert: { message: alertMessage }
    }),
    [search.value, search.onChange, alertMessage]
  );

  return <ExpeditionQueueStatusBase list={list} slots={slots} loading={state.isLoading} />;
}

type MissingItemProps = {
  attendance: VehiclesMissingData;
};
function MissingItem({ attendance }: MissingItemProps) {
  const { expedition } = useSession();
  const expeditionCompanyId = expedition?.company.id;

  const [qtyTransported, setQtyTransported] = useState(attendance.scheduling?.qtyTransported?.toString() || "");
  const [orderId, setOrderId] = useState(attendance.scheduling?.id || "");
  const [loading, { open: setLoading, close: unsetLoading }] = useDisclosure(false);
  const [recover, { close: closeRecover, toggle: toggleRecover }] = useDisclosure(false);

  const today = dayjs().toString();
  const compareAttendendedAt = useMemo(() => dayjs(attendance.attended_at).isSame(today, "day"), [attendance.attended_at, today]);
  const companyId = expedition?.company.id;
  const role = expedition?.role;
  const isExpeditionViewer = role === Role.Espectador;
  const isConciergeViewer = role === Role.Portaria;

  const handleOption = (value: Primitive | null) => {
    if (!value) return;

    setOrderId(Number(value));
  };

  const handleSave = async () => {
    const schema = z.object({
      qtyTransported: z.string().min(1, "Peso líquido é obrigatório!"),
      orderId: z.string().min(1, "Selecione um pedido!")
    });

    setLoading();
    try {
      await schema.parseAsync({ qtyTransported, orderId: String(orderId) });

      const body = {
        orderId: Number(orderId),
        queueId: attendance.id,
        qtyTransported: Number(qtyTransported.replace(",", ".")),
        company: { id: companyId }
      };
      // alert(JSON.stringify(body, null, 2));

      await api.post("/queue-restore/save", body);

      toast.success(`Solicitação enviada!`);
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((error) => {
          toast.warning(error.message);
        });
        return;
      }

      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message || "Erro desconhecido");
        return;
      }

      toast.error("Erro desconhecido");
      console.error(error);
    } finally {
      unsetLoading();
      closeRecover();
      setQtyTransported("");
      setOrderId("");
      //TODO Adicionar função para refetch de motoristas faltosos como o hook useRefetch
      // refetch();
    }
  };

  const [trigger, { data, isFetching }] = useLazyGetOrdersQuery();
  const innerOrders = data?.map((order) => ({ value: String(order.id), label: order.code }));

  const fetchOrders = (val?: string) => {
    const body: { company: number; filter?: string } = { company: expeditionCompanyId! };

    if (val) {
      body.filter = val;
    }

    trigger(body, true);
  };

  useEffect(() => {
    if (expeditionCompanyId) {
      fetchOrders();
    }
  }, []);

  return (
    <ExpeditionQueueListItemBase
      driverName={attendance?.driver?.name}
      slots={{
        Plate: { plate: attendance.truck.licensePlate },
        ExpeditionDate: { date: attendance.attended_at }
      }}
      components={{
        Actions: (
          <>
            {!isConciergeViewer && !isExpeditionViewer && compareAttendendedAt && (
              <ExpeditionActionsBase
                state={{ active: recover, loading }}
                component={{ Icon: { Save: FloppyDiskIcon, Cancel: ArrowClockwiseIcon } }}
                actions={{ cancel: closeRecover, toggleActive: toggleRecover, save: handleSave }}
              />
            )}
          </>
        ),
        DriverInfo: (
          <>
            {recover && (
              <div className="flex items-center justify-center gap-2">
                <Input.Uncontrolled.Text
                  placeholder="Peso Líquido"
                  value={qtyTransported}
                  onChange={(e) => {
                    const txt = handleFormatTxtToQty(e.currentTarget.value);
                    setQtyTransported(txt);
                  }}
                  rightSection="t"
                  size="xs"
                  radius="sm"
                />

                <Input.Uncontrolled.Select
                  data={innerOrders}
                  placeholder="Selecione o Pedido"
                  size="xs"
                  radius="sm"
                  value={String(orderId)}
                  onChange={(value) => handleOption(value)}
                  onClear={() => setOrderId("")}
                  clearable={false}
                  onSearchChange={fetchOrders}
                  leftSection={isFetching && <Loader size="xs" />}
                />
              </div>
            )}
          </>
        )
      }}
    />
  );
}
